import { CircleInfoIcon } from "@xola/icons";
import { flash, SubmitButton, Switch } from "@xola/ui-kit";
import { requiredNumber, SecondaryButton } from "common";
import { FormikProps } from "formik";
import pluralize from "pluralize";
import React, { useRef } from "react";
import { useMutation, useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { Merchandise, OversellPolicy, VariantGroup } from "types";
import { boolean, mixed, number, object } from "yup";
import { MAX_VARIANT_GROUPS, VariantGroupsTable } from "./Inventory/VariantGroupsTable";
import {
    fetchPendingUnfulfilledMerchandisePurchases,
    getInventoryInitialValues,
    getMerchandiseInventoryPayload,
    hasVariantGroupStructureChanged,
    MerchandiseInventoryFormValues,
} from "./merchandiseHelper";
import { api } from "@/api/api";
import { ConfirmLeaveModal } from "@/components/Common/ConfirmLeaveModal";
import { Form } from "@/components/Common/Form";
import { PromptModal } from "@/components/Common/PromptModal";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useModal } from "@/hooks/useVisibility";
import { MERCHANDISE_API_PATH, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

const validationSchema = object().shape({
    trackQuantity: boolean(),
    quantity: number().when("trackQuantity", {
        is: true,
        then: requiredNumber(),
    }),
    notifyLowInventory: boolean(),
    lowInventoryThreshold: number().when("notifyLowInventory", {
        is: true,
        then: requiredNumber(),
    }),
    oversellPolicy: mixed<OversellPolicy>().oneOf(Object.values(OversellPolicy)),
    hasVariants: boolean(),
});

export const MerchandiseInventory = ({ merchandise }: { merchandise: Merchandise }) => {
    const history = useHistory();
    const formikRef = useRef<FormikProps<MerchandiseInventoryFormValues>>(null);
    const warningModal = useModal();
    const pendingValuesRef = useRef<MerchandiseInventoryFormValues | null>(null);
    const initialValues = getInventoryInitialValues(merchandise);
    const hasExistingCatalogItems = (merchandise.catalog?.items?.length ?? 0) > 0;
    const seller = getSeller();

    // Fetch unfulfilled purchases for this merchandise (first 100 only)
    const {
        data: unfulfilledPurchasesResponse,
        isLoading: isLoadingUnfulfilledPurchases,
        isError: isUnfulfilledPurchasesError,
    } = useQuery(
        ["unfulfilledPurchases", seller.id, merchandise.id],
        async () => fetchPendingUnfulfilledMerchandisePurchases(seller.id, merchandise.id),
        { enabled: hasExistingCatalogItems, refetchOnMount: "always" },
    );

    const unfulfilledPurchaseCount = unfulfilledPurchasesResponse?.data?.length ?? 0;
    const unfulfilledPurchasesLabel = isUnfulfilledPurchasesError
        ? "error finding unfulfilled purchases for this product"
        : unfulfilledPurchasesResponse?.paging?.next
        ? "100+ purchases"
        : `${unfulfilledPurchaseCount} ${pluralize("purchase", unfulfilledPurchaseCount)}`;

    const { mutateAsync: saveInventory, isLoading: isSavingInventory } = useMutation(
        async (values: MerchandiseInventoryFormValues) => {
            const payload = getMerchandiseInventoryPayload(values, merchandise.warePreference);
            const { data } = await api.put<Merchandise>(`${MERCHANDISE_API_PATH}/${merchandise?.id}`, payload);

            flash.success("Variants & Inventory saved successfully");
            updateCache(MERCHANDISE_CACHE_KEY, data);
            // If product is multiple-variant, backend will process updates async.
            history.push(`/products/merchandise/${merchandise.id}/items`);
        },
    );

    const shouldConfirmBeforeSave = (values: MerchandiseInventoryFormValues): boolean => {
        if (!hasExistingCatalogItems) {
            return false;
        }

        if (!isLoadingUnfulfilledPurchases && !isUnfulfilledPurchasesError && unfulfilledPurchaseCount === 0) {
            return false;
        }

        const isInventoryDisabled = initialValues.trackQuantity && !values.trackQuantity;
        const hasVariantChanges = hasVariantGroupStructureChanged(initialValues.variantGroups, values.variantGroups);
        return isInventoryDisabled || hasVariantChanges;
    };

    const handleSave = async (values: MerchandiseInventoryFormValues) => {
        if (shouldConfirmBeforeSave(values)) {
            pendingValuesRef.current = values;
            warningModal.show();
            return;
        }

        await saveInventory(values);
    };

    const handleConfirmSave = async () => {
        const pendingValues = pendingValuesRef.current;
        if (!pendingValues) {
            warningModal.hide();
            return;
        }

        warningModal.hide();
        pendingValuesRef.current = null;
        await saveInventory(pendingValues);
    };

    const handleCancelConfirmSave = () => {
        pendingValuesRef.current = null;
        warningModal.hide();
    };

    return (
        <>
            <XolaFormik
                enableReinitialize
                enablePrompt={false}
                initialValues={initialValues}
                validationSchema={validationSchema}
                innerRef={formikRef}
                onSubmit={handleSave}
            >
                {({ isSubmitting, dirty, values, setFieldValue, handleReset }) => {
                    const handleSaveVariantGroup = (group: VariantGroup, editingIndex?: number) => {
                        if (editingIndex !== undefined) {
                            const updatedGroups = [...values.variantGroups];
                            updatedGroups[editingIndex] = group;
                            setFieldValue("variantGroups", updatedGroups);
                        } else {
                            if (values.variantGroups.length >= MAX_VARIANT_GROUPS) {
                                return;
                            }
                            setFieldValue("variantGroups", [...values.variantGroups, group]);
                        }
                    };

                    const handleDeleteGroup = (groupIndex: number) => {
                        const updatedGroups = values.variantGroups.filter((_, index) => index !== groupIndex);
                        setFieldValue("variantGroups", updatedGroups);
                    };

                    const handleReorderGroups = (reorderedGroups: VariantGroup[]) => {
                        setFieldValue("variantGroups", reorderedGroups);
                    };

                    return (
                        <Form>
                            <ConfirmLeaveModal isFormTouched={dirty && !isSubmitting && !isSavingInventory} />
                            <ScrollToFieldError />
                            <ContentLayout isWide>
                                <div className="space-y-4">
                                    <h3>Variants & Inventory Tracking</h3>

                                    <div className="space-y-2">
                                        <h6 className="font-bold">Inventory</h6>
                                        <Switch.Group>
                                            <Switch
                                                isChecked={values.trackQuantity}
                                                onChange={(checked: boolean) => setFieldValue("trackQuantity", checked)}
                                            />
                                            <Switch.Label className="ml-3">Track inventory for this item</Switch.Label>
                                        </Switch.Group>
                                    </div>

                                    <div className="space-y-2">
                                        <h6 className="font-bold">Variants</h6>
                                        <Switch.Group>
                                            <Switch
                                                data-testid="variants-switch"
                                                isChecked={values.hasVariants}
                                                onChange={(checked: boolean) => setFieldValue("hasVariants", checked)}
                                            />
                                            <Switch.Label className="ml-3">
                                                Create multiple variants of this item (e.g. small, medium, large)
                                            </Switch.Label>
                                        </Switch.Group>
                                    </div>

                                    {values.hasVariants && (
                                        <VariantGroupsTable
                                            variantGroups={values.variantGroups}
                                            onReorder={handleReorderGroups}
                                            onDelete={handleDeleteGroup}
                                            onSave={handleSaveVariantGroup}
                                        />
                                    )}
                                </div>
                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton isLoading={isSubmitting || isSavingInventory}>Save</SubmitButton>
                                    <SecondaryButton type="reset" onClick={handleReset}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>
                            </ContentLayout>
                        </Form>
                    );
                }}
            </XolaFormik>
            <PromptModal
                isOpen={warningModal.isVisible}
                title="Inventory or variant changes may affect unfulfilled purchases"
                isLoading={isSavingInventory}
                dialogText={{ secondary: "Cancel", submit: "Continue" }}
                onClose={handleCancelConfirmSave}
                onConfirm={handleConfirmSave}
            >
                <div className="space-y-4">
                    <p>Are you sure you want to continue?</p>

                    <div className="flex items-center space-x-3 rounded-lg bg-danger-lighter p-4">
                        <CircleInfoIcon color="text-danger" size="medium" className="shrink-0 self-center" />
                        <div className="space-y-2">
                            <p className="font-bold">
                                There are <var data-var="unfulfilledPurchasesLabel">{unfulfilledPurchasesLabel}</var>{" "}
                                with unfulfilled items. Updating might affect their inventory.
                            </p>
                        </div>
                    </div>
                </div>
            </PromptModal>
        </>
    );
};
